import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

/* Štampa ulazne fakture = SEF-ova verzija dokumenta + naša traka u vrhu.

   Zašto SEF-ova verzija, a ne naš ispis: papir koji ide u registrator mora da
   bude ono što je dobavljač poslao i što je država videla. Naš ispis bi bio
   prepis — a prepis se u kontroli ne priznaje.

   Traka u gornjem desnom uglu nosi ono što na dobavljačevom papiru ne postoji,
   a nama treba na prvi pogled: naš delovodni broj (da se nalepi/upiše), ko je
   proveru potvrdio i kada je faktura prihvaćena na SEF-u.

   Font se ugrađuje iz .ttf — standardni Helvetica iz pdf-lib nema č, ć, š, ž,
   pa bi imena i nazivi izlazili nagrđeni. (.woff ne radi sa fontkit-om, .ttf da.) */
const FONT_URL = process.env.PUBLIC_URL + "/AETR/LiberationSans-Regular.ttf";

const SIVA = rgb(0.42, 0.42, 0.42);
const CRNA = rgb(0.1, 0.1, 0.1);
const OKVIR = rgb(0.75, 0.75, 0.75);
const PODLOGA = rgb(0.97, 0.97, 0.97);

const base64UBajtove = (b64) =>
    Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/**
 * @param {object} o
 * @param {string} o.pdfBase64   SEF-ov PDF fakture
 * @param {string} o.nasBroj     naš delovodni broj (UF RS-1/26…)
 * @param {string} [o.proverio]  ime onoga ko je potvrdio proveru
 * @param {string} [o.datumProvere]
 * @param {string} [o.prihvaceno] kad je prihvaćena na SEF-u
 * @param {string} [o.dobavljac]
 * @returns {Promise<Uint8Array>} PDF spreman za štampu
 */
export const pripremiStampuUlazne = async ({
    pdfBase64, nasBroj, proverio, datumProvere, prihvaceno, dobavljac,
}) => {
    const [pdfDoc, fontBytes] = await Promise.all([
        PDFDocument.load(base64UBajtove(pdfBase64)),
        fetch(FONT_URL).then((r) => {
            if (!r.ok) throw new Error("Font za štampu nije dostupan.");
            return r.arrayBuffer();
        }),
    ]);

    pdfDoc.registerFontkit(fontkit);
    const font = await pdfDoc.embedFont(fontBytes, { subset: true });

    const strana = pdfDoc.getPages()[0];
    if (!strana) throw new Error("PDF sa SEF-a nema nijednu stranu.");

    const { width, height } = strana.getSize();

    /* Redovi trake. Prazni se izostavljaju — bolje kraća traka nego prazne
       etikete koje ništa ne govore. */
    const redovi = [
        { naziv: "Naš broj:",   vrednost: nasBroj || "—", istaknuto: true },
        proverio     ? { naziv: "Proverio:",     vrednost: proverio } : null,
        datumProvere ? { naziv: "Datum provere:", vrednost: datumProvere } : null,
        prihvaceno   ? { naziv: "Prihvaćeno na SEF-u:", vrednost: prihvaceno } : null,
        dobavljac    ? { naziv: "Dobavljač:",    vrednost: dobavljac } : null,
    ].filter(Boolean);

    const VEL_NASLOV = 11;
    const VEL_TEKST = 8;
    const RAZMAK = 11;
    const PAD = 8;

    /* Širina se računa iz najšireg reda, da tekst ne izađe iz okvira ni kad je
       ime dugačko. Gornja granica je trećina strane — traka je oznaka, ne
       drugi dokument. */
    const sirinaReda = (r) =>
        font.widthOfTextAtSize(r.naziv + " ", VEL_TEKST) +
        font.widthOfTextAtSize(String(r.vrednost), r.istaknuto ? VEL_NASLOV : VEL_TEKST);

    const sirina = Math.min(
        Math.max(...redovi.map(sirinaReda)) + PAD * 2,
        width / 3
    );
    const visina = redovi.length * RAZMAK + PAD * 2 + 4;

    const x = width - sirina - 18;
    const y = height - visina - 18;

    strana.drawRectangle({
        x, y, width: sirina, height: visina,
        color: PODLOGA, borderColor: OKVIR, borderWidth: 0.8,
    });

    let ty = y + visina - PAD - VEL_NASLOV + 2;
    redovi.forEach((r) => {
        const vel = r.istaknuto ? VEL_NASLOV : VEL_TEKST;
        strana.drawText(r.naziv, { x: x + PAD, y: ty, size: VEL_TEKST, font, color: SIVA });
        const pomak = font.widthOfTextAtSize(r.naziv + " ", VEL_TEKST);
        strana.drawText(String(r.vrednost), {
            x: x + PAD + pomak, y: ty, size: vel, font, color: CRNA,
        });
        ty -= RAZMAK;
    });

    return pdfDoc.save();
};

/* Otvara odštampanu verziju u novoj kartici sa pozvanim dijalogom za štampu.
   Blob se oslobađa sa zadrškom — oslobođen odmah, prozor bi ostao prazan. */
export const odstampaj = async (podaci) => {
    const bajtovi = await pripremiStampuUlazne(podaci);
    const url = URL.createObjectURL(new Blob([bajtovi], { type: "application/pdf" }));
    const prozor = window.open(url, "_blank");
    if (prozor) {
        prozor.addEventListener("load", () => prozor.print(), { once: true });
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return !!prozor;
};
